import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wallet, ArrowUpRight, ArrowDownRight, History, CreditCard, 
  Plus, RefreshCw, TrendingUp, Shield, AlertCircle,
  ArrowLeft, Home, Search, Bell, User
} from 'lucide-react';
import Loading from '../../components/common/Loading';
import { getWalletBalance, getTransactionHistory, getPayoutMethods } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

const WalletDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [walletData, setWalletData] = useState({
    balance: 0,
    pendingBalance: 0
  });
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [payoutMethods, setPayoutMethods] = useState([]);
  const [error, setError] = useState(null);

  const colors = {
    primary: '#ff660e',
    black: '#0f0e0e',
    white: '#ffffff',
    lightBg: '#f8f9fa'
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchWalletData();
  }, [user, navigate]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch wallet balance
      const balanceRes = await getWalletBalance();
      
      if (balanceRes && balanceRes.success) {
        setWalletData({
          balance: parseFloat(balanceRes.balance) || 0,
          pendingBalance: parseFloat(balanceRes.pending_balance) || 0
        });
      }
  
      // Fetch recent transactions
      const transactionsRes = await getTransactionHistory();
      
      let transactions = [];
  
      if (transactionsRes && transactionsRes.success) {
        if (Array.isArray(transactionsRes.transactions)) {
          transactions = transactionsRes.transactions;
        } else if (Array.isArray(transactionsRes.data)) {
          transactions = transactionsRes.data;
        }
        
        setRecentTransactions(transactions);
      }
  
      // Fetch payout methods - FIXED VERSION
      const payoutRes = await getPayoutMethods(); // Pass userId!
      
      if (payoutRes && payoutRes.success) {
        const methods = (payoutRes.payout_methods || []).map(method => {
          let details = {};
          try {
            if (typeof method.details === 'string') {
              details = JSON.parse(method.details);
            } else if (method.details && typeof method.details === 'object') {
              details = method.details;
            }
          } catch (e) {
            // console.error('Error parsing method details:', e);
            details = {};
          }
          
          return {
            ...method,
            details: details,
            verification_status: method.verification_status || {}
          };
        });
        
        setPayoutMethods(methods);
      } else {
        setPayoutMethods([]);
      }
    } catch (error) {
      // console.error('Error fetching wallet data:', error);
      setError('Failed to load wallet data. Please try again.');
      setRecentTransactions([]);
      setPayoutMethods([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWalletData();
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-600 bg-gray-50 border-gray-200';
    
    switch (status.toLowerCase()) {
      case 'success':
      case 'successful':
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
      case 'processing':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'failed':
      case 'declined':
      case 'abandoned':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTransactionIcon = (type) => {
    if (!type) return <CreditCard className="w-5 h-5 text-gray-500" />;
    
    switch (type.toLowerCase()) {
      case 'deposit':
        return <ArrowDownRight className="w-5 h-5 text-green-500" />;
      case 'withdrawal':
      case 'withdraw':
        return <ArrowUpRight className="w-5 h-5 text-red-500" />;
      default:
        return <CreditCard className="w-5 h-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className="lg:hidden min-h-screen bg-gray-50 pb-20">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <h1 className="text-lg font-bold text-black">Wallet</h1>
            
            <button
              onClick={handleRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        {/* Balance Card */}
        <div className="bg-gradient-to-r from-[#ff660e] to-orange-500 rounded-xl p-5 mb-4 text-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm opacity-90">Available Balance</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(walletData.balance)}</p>
            </div>
            <Wallet className="w-10 h-10 opacity-80" />
          </div>
          
          {walletData.pendingBalance > 0 && (
            <div className="bg-white/20 rounded-lg p-3">
              <p className="text-sm">Pending Balance</p>
              <p className="text-lg font-semibold">{formatCurrency(walletData.pendingBalance)}</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => navigate('/wallet/deposit')}
            className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:shadow-sm transition-shadow"
          >
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <ArrowDownRight className="w-6 h-6 text-green-600" />
            </div>
            <p className="font-semibold text-black">Deposit</p>
            <p className="text-sm text-gray-600 mt-1">Add funds</p>
          </button>
          
          <button
            onClick={() => navigate('/wallet/withdraw')}
            className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:shadow-sm transition-shadow"
          >
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <ArrowUpRight className="w-6 h-6 text-red-600" />
            </div>
            <p className="font-semibold text-black">Withdraw</p>
            <p className="text-sm text-gray-600 mt-1">Transfer out</p>
          </button>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-black">Recent Transactions</h2>
            <button
              onClick={() => navigate('/wallet/transactions')}
              className="text-sm text-[#ff660e] font-medium"
            >
              View All
            </button>
          </div>
          
          {recentTransactions.length > 0 ? (
            <div className="space-y-3">
              {recentTransactions.slice(0, 3).map((transaction, index) => (
                <div
                  key={transaction.transaction_id || transaction.id || `transaction-${index}`}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-gray-100">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-black">
                        {transaction.description || 
                         (transaction.transaction_type === 'deposit' ? 'Deposit' : 
                          transaction.transaction_type === 'withdrawal' ? 'Withdrawal' : 
                          'Transaction')}
                      </p>
                      <p className="text-xs text-gray-500">{formatDate(transaction.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold text-sm ${
                      transaction.type?.toLowerCase() === 'credit' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type?.toLowerCase() === 'credit' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}
                    </p>
                    {transaction.status && (
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">No transactions yet</p>
              <button
                onClick={() => navigate('/wallet/deposit')}
                className="mt-3 px-4 py-2 bg-[#ff660e] text-white rounded-lg font-medium text-sm"
              >
                Make Your First Deposit
              </button>
            </div>
          )}
        </div>

        {/* Payout Methods */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-black">Payout Methods</h2>
            <button
              onClick={() => navigate('/wallet/payout-methods')}
              className="text-sm text-[#ff660e] font-medium"
            >
              Manage
            </button>
          </div>
          
          {payoutMethods.length > 0 ? (
            <div className="space-y-3">
              {payoutMethods.slice(0, 2).map((method, index) => {
                let details = {};
                try {
                  if (typeof method.details === 'string') {
                    details = JSON.parse(method.details);
                  } else if (method.details && typeof method.details === 'object') {
                    details = method.details;
                  }
                } catch (e) {
                  // console.error('Error parsing method details:', e);
                }
                
                return (
                  <div
                    key={method.payout_method_id || method.id || `method-${index}`}
                    className="p-3 border rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-[#ff660e]" />
                        <span className="font-medium">{details.bank_name || 'Bank Account'}</span>
                      </div>
                      {method.is_default === 1 && (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    {details.account_number && (
                      <p className="text-gray-700 font-mono text-sm">{details.account_number}</p>
                    )}
                    {details.account_name && (
                      <p className="text-xs text-gray-500">{details.account_name}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-3">No payout methods added yet</p>
              <button
                onClick={() => navigate('/wallet/payout-methods')}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-[#ff660e] text-white rounded-lg font-medium text-sm mx-auto"
              >
                <Plus className="w-4 h-4" />
                Add Payout Method
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block min-h-screen bg-gray-50">
      {/* Desktop Header */}
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-black">Wallet Dashboard</h1>
            <p className="text-gray-600">Manage your funds and transactions</p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Balance & Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Balance Card */}
            <div className="bg-gradient-to-r from-[#ff660e] to-orange-500 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm opacity-90">Available Balance</p>
                  <p className="text-4xl font-bold mt-2">{formatCurrency(walletData.balance)}</p>
                </div>
                <Wallet className="w-12 h-12 opacity-80" />
              </div>
              
              {walletData.pendingBalance > 0 && (
                <div className="bg-white/20 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm">Pending Balance</p>
                      <p className="text-xl font-semibold">{formatCurrency(walletData.pendingBalance)}</p>
                    </div>
                    <TrendingUp className="w-6 h-6" />
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/wallet/deposit')}
                  className="flex-1 bg-white text-[#ff660e] py-3.5 rounded-xl font-bold hover:bg-gray-100 flex items-center justify-center gap-2"
                >
                  <ArrowDownRight className="w-5 h-5" />
                  Deposit Funds
                </button>
                
                <button
                  onClick={() => navigate('/wallet/withdraw')}
                  className="flex-1 bg-transparent border-2 border-white text-white py-3.5 rounded-xl font-bold hover:bg-white/10 flex items-center justify-center gap-2"
                >
                  <ArrowUpRight className="w-5 h-5" />
                  Withdraw Funds
                </button>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-black">Recent Transactions</h2>
                <button
                  onClick={() => navigate('/wallet/transactions')}
                  className="text-[#ff660e] hover:text-[#ff5500] font-medium"
                >
                  View All Transactions
                </button>
              </div>
              
              {recentTransactions.length > 0 ? (
                <div className="space-y-3">
                  {recentTransactions.slice(0, 5).map((transaction, index) => (
                    <div
                      key={transaction.transaction_id || transaction.id || `transaction-${index}`}
                      onClick={() => navigate(`/wallet/transactions/${transaction.transaction_id}`)}
                      className="flex items-center justify-between p-4 border rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-gray-100">
                          {getTransactionIcon(transaction.type)}
                        </div>
                        <div>
                          <p className="font-semibold text-black">
                            {transaction.description || 
                             (transaction.transaction_type === 'deposit' ? 'Deposit' : 
                              transaction.transaction_type === 'withdrawal' ? 'Withdrawal' : 
                              'Transaction')}
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <p className="text-sm text-gray-500">
                              {formatDate(transaction.created_at || transaction.date)}
                            </p>
                            {transaction.reference && (
                              <p className="text-sm text-gray-500">
                                Ref: {transaction.reference.substring(0, 10)}...
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-lg ${
                          transaction.type?.toLowerCase() === 'credit' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {transaction.type?.toLowerCase() === 'credit' ? '+' : '-'}
                          {formatCurrency(transaction.amount)}
                        </p>
                        <div className="mt-2">
                          {transaction.status && (
                            <span className={`px-3 py-1.5 text-sm rounded-full ${getStatusColor(transaction.status)}`}>
                              {transaction.status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions yet</h3>
                  <p className="text-gray-600 mb-6">Start by making your first deposit</p>
                  <button
                    onClick={() => navigate('/wallet/deposit')}
                    className="bg-[#ff660e] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#ff5500]"
                  >
                    Make Your First Deposit
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Payout Methods & Info */}
          <div className="space-y-6">
            {/* Payout Methods */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-black">Payout Methods</h2>
                <button
                  onClick={() => navigate('/wallet/payout-methods')}
                  className="text-[#ff660e] hover:text-[#ff5500] font-medium"
                >
                  Manage
                </button>
              </div>
              
              {payoutMethods.length > 0 ? (
                <div className="space-y-4">
                  {payoutMethods.map((method, index) => {
                    let details = {};
                    try {
                      if (typeof method.details === 'string') {
                        details = JSON.parse(method.details);
                      } else if (method.details && typeof method.details === 'object') {
                        details = method.details;
                      }
                    } catch (e) {
                      // console.error('Error parsing method details:', e);
                    }
                    
                    return (
                      <div
                        key={method.payout_method_id || method.id || `method-${index}`}
                        className="p-4 border rounded-xl hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <CreditCard className="w-5 h-5 text-[#ff660e]" />
                            <span className="font-semibold">{details.bank_name || 'Bank Account'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {method.is_default === 1 && (
                              <span className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                Default
                              </span>
                            )}
                            {method.is_verified ? (
                              <Shield className="w-4 h-4 text-green-500" />
                            ) : (
                              <span className="text-xs text-yellow-600">Pending</span>
                            )}
                          </div>
                        </div>
                        {details.account_number && (
                          <p className="text-gray-800 font-mono text-lg">{details.account_number}</p>
                        )}
                        {details.account_name && (
                          <p className="text-sm text-gray-600">{details.account_name}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No payout methods added yet</p>
                  <button
                    onClick={() => navigate('/wallet/payout-methods')}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-[#ff660e] text-white rounded-lg font-semibold hover:bg-[#ff5500] mx-auto"
                  >
                    <Plus className="w-5 h-5" />
                    Add Payout Method
                  </button>
                </div>
              )}
            </div>

            {/* Security Info */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
                <h3 className="font-bold text-black">Security Features</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Bank-level encryption</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Instant transaction alerts</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">24/7 fraud monitoring</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Secure payout methods</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-bold text-black mb-4">Quick Links</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => navigate('/wallet/transactions')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <History className="w-5 h-5 text-[#ff660e]" />
                    <span>Transaction History</span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-gray-400" />
                </button>
                <button 
                  onClick={() => navigate('/wallet/deposit')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <ArrowDownRight className="w-5 h-5 text-[#ff660e]" />
                    <span>Deposit Funds</span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-gray-400" />
                </button>
                <button 
                  onClick={() => navigate('/wallet/withdraw')}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <ArrowUpRight className="w-5 h-5 text-[#ff660e]" />
                    <span>Withdraw Funds</span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Desktop Footer */}
      <Footer />
    </div>
  );

  return (
    <>
      {/* Render Mobile or Desktop View */}
      <MobileView />
      <DesktopView />
    </>
  );
};

export default WalletDashboard;